import { CustomFieldResponses } from './invites';

export interface ValidProfileDataMap {
  [key: string]: boolean;
}

export interface SharedBusinessData {
  propertyIds: string[];
  customFieldResponses: CustomFieldResponses;
  dataForMembers: string[];
}

export enum MobileOS {
  IOS = 'IOS',
  ANDROID = 'ANDROID',
  UNKNOWN = 'UNKNOWN',
}

export interface Auth {
  alias: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

export enum LinkType {
  MAGIC_LINK = 'MAGIC_LINK',
  CONNECTION_INVITE = 'CONNECTION_INVITE',
  COMMUNITY_INVITE = 'COMMUNITY_INVITE',
  PERSONAL_INVITE = 'PERSONAL_INVITE',
  BUSINESS_INVITE = 'BUSINESS_INVITE',
  MOMENTS_INVITE = 'MOMENTS_INVITE',
  PERSONAL_LINK = 'PERSONAL_LINK',
  LOCAL_CONTACT = 'LOCAL_CONTACT',
  EXTERNAL_MEMBER_INVITE = 'EXTERNAL_MEMBER_INVITE',
  EVENT_INVITE = 'EVENT_INVITE',
  EVENT_DATEPICKER_INVITE = 'EVENT_DATEPICKER_INVITE',
  PAYMENT_LINK = 'PAYMENT_LINK',
}

/**
 * Information about a redirect link
 * including the id of the link and the
 * type of the link
 */
export interface LinkInfo {
  type: LinkType;
  data: any;
  linkId: string;
}

export type LinkInfoWithId = LinkInfo & { linkId: string };

export enum EmailPreferenceType {
  INTRODUCTIONEMAIL = 'INTRODUCTIONEMAIL',
  NEWCONNECTIONEMAIL = 'NEWCONNECTIONEMAIL',
  DATAREQUESTEMAIL = 'DATAREQUESTEMAIL',
  COMMUNITYEMAIL = 'COMMUNITYEMAIL',
  CONNECTION_REQUESTS = 'CONNECTION_INVITE',
  INTRODUCTIONS = 'INTRODUCTION',
  EVENT_INVITES = 'EVENT_INVITE',
  MARKETING_AUTOMATION = 'MARKETING_AUTOMATION',
}

export interface CommunityMember {
  /**
   * The user id of the member.
   */
  id: number;
  /**
   * The member's first name.
   */
  firstName: string;
  /**
   * The member's last name.
   */
  lastName: string;
  /**
   * The member's picture.
   */
  picture: string;
  /**
   * The member role in the community.
   */
  role: CommunityRole;
}

export enum CommunityRole {
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',
}

export interface CommunityData {
  /**
   * The id of the community
   */
  id: number;

  /**
   * The businessId of the business connected to the community
   */
  businessId: number;

  /**
   * The name of the community
   */
  name: string;

  /**
   * The community picture
   */
  picture: string;

  /**
   * The number of members in the community
   */
  memberCount: number;

  /**
   * The community description
   */
  description: string;

  /**
   *
   */
  link: string;

  /**
   * The role of the user within the specific community.
   */
  role: CommunityRole;

  /**
   * The type of community.
   */
  type: CommunityType;

  /**
   * Enabled features for this community
   */
  features?: CommunityFeatures;

  /**
   * Member permissions for different operations
   */
  memberPermissions?: CommunityMemberPermissions;
}

export type CommunityFeatures = {
  EVENTS?: boolean;
  PHOTO_ALBUMS?: boolean;
  TASKS?: boolean;
  STATS_AND_SCORES?: boolean;
  MEMBER_ADMINISTRATION?: boolean;
  EASY_SHARING?: boolean;
  COST_SPLITTING?: boolean;
  DOCUMENTS?: boolean;
  COMMUNICATION?: boolean;
  FILES?: boolean;
  FORMS?: boolean;
};

export type DashboardFeatures = {
  MEMBER_ADMINISTRATION?: boolean;
  PARTICIPATION_INSIGHTS?: boolean;
  COMMUNICATION?: boolean;
  FINANCIAL_ADMINISTRATION?: boolean;
};

export type CommunityMemberPermissions = {
  [CATEGORY in 'EVENTS' | 'MEMBERS' | 'STATS_AND_SCORES' | 'TASKS' | 'PHOTO_ALBUMS' | 'COST_SPLITTING']?: {
    [OPERATION in 'CREATE' | 'UPDATE' | 'DELETE']?: boolean;
  };
};

export enum CommunityType {
  PRIVATE = 'PRIVATE',
  PUBLIC = 'PUBLIC',
  BUSINESS = 'BUSINESS',
  ROOMMATES = 'ROOMMATES',
  STUDENTGROUP = 'STUDENTGROUP',
  SPORTTEAM = 'SPORTTEAM',
  ASSOCIATION = 'ASSOCIATION',
  COLLEAGUES = 'COLLEAGUES',
  FAMILY = 'FAMILY',
  FRIENDS = 'FRIENDS',
  OTHER_COMMUNITY = 'OTHER_COMMUNITY',
}

export enum SubscriptionType {
  FREE = 'COMMUNITY_FREE',
  BASE = 'COMMUNITY_BASE',
  PRO = 'COMMUNITY_PRO',
}

export enum BusinessSubscriptionType {
  STARTER = 'BUSINESS_STARTER',
  PROFESSIONAL = 'BUSINESS_PROFESSIONAL',
}

export enum OnboardingStep {
  INIT = 0,
  OVERVIEW = 1,
  COMMUNICATION = 2,
  EVENTS = 3,
  SETTINGS = 4,
  INVITE = 5,
}

/**
 * Personal connection type for connection overview
 */
export interface PersonalConnection {
  /**
   * The id of the connection
   */
  id: number;
  /**
   * The first name of the connection
   */
  firstName: string;
  /**
   * The last name of the connection
   */
  lastName: string;
  /**
   * The profile picture of the connection
   */
  picture?: string;
  /**
   * The email of the connection
   */
  email?: string;
  /**
   * The community where this connection was made
   */
  community?: {
    id: number;
    name: string;
  };
  /**
   * When the connection was created
   */
  createdAt: string;
}
